import { formatDelta, formatValue } from '@pp/shared';
import './MetricTile.css';

/**
 * What a tile needs to draw itself. `StudentMetric` satisfies this
 * structurally (it carries a `key` the tile itself never reads), and so
 * does a studio's own custom metric, which has no fixed MetricKey.
 */
export interface TileMetric {
  label: string;
  unit: string;
  decimals: number;
  currentValue: number;
  currentDate: string;
  previousValue: number | null;
  previousDate: string | null;
  changeAbs: number | null;
  blockedReason: string | null;
}

/**
 * How long ago the comparison period started, from the two real dates the
 * metric already carries — never a guess, never a fixed "son 3 ayda" label.
 */
function spanLabel(previousDate: string, currentDate: string): string {
  const days = Math.round(
    (new Date(currentDate).getTime() - new Date(previousDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );
  if (days <= 0) return '';
  if (days < 45) return `Son ${days} günde`;
  const months = Math.round(days / 30);
  return `Son ${months} ayda`;
}

/**
 * One measurement and how it moved.
 *
 * The change is never coloured red or green and never carries a bare arrow
 * (Doc 6 §5.3). Falling weight is progress and rising muscle mass is progress;
 * encoding direction as colour would mislead. The sign carries the meaning.
 */
export function MetricTile({ metric }: { metric: TileMetric }) {
  return (
    <div className="tile">
      <span className="tile__label">{metric.label}</span>
      <span className="tile__value">
        {formatValue(metric.currentValue, metric.unit, metric.decimals)}
      </span>

      {metric.changeAbs !== null && metric.previousDate ? (
        <span className="tile__delta-row">
          <span className="tile__delta">
            {formatDelta(metric.changeAbs, metric.unit, metric.decimals)}
          </span>
          <span className="tile__span">
            {spanLabel(metric.previousDate, metric.currentDate)}
          </span>
        </span>
      ) : metric.blockedReason === 'MUSCLE_UNIT_MISMATCH' ? (
        <span className="tile__note">Birim değiştiği için karşılaştırılamıyor</span>
      ) : (
        <span className="tile__note">İlk ölçüm</span>
      )}
    </div>
  );
}
